/* ==========================================================================
   FLC Forge — Global JS
   ========================================================================== */

'use strict';

/* Utility helpers
   ========================================================================== */
const FLC = {
  /** Debounce a function */
  debounce(fn, delay = 250) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  },

  /** Throttle a function */
  throttle(fn, limit = 100) {
    let lastRun = 0;
    return (...args) => {
      const now = Date.now();
      if (now - lastRun >= limit) { lastRun = now; fn(...args); }
    };
  },

  /** Format money using Shopify money format */
  formatMoney(cents, format = window.flcForge.moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = parseInt(cents, 10) / 100;
    const withCommas = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount         = withCommas(value.toFixed(2).replace(/\.00$/, ''));
    const amountNoDecimal = withCommas(String(Math.round(value)));
    const amountCommaSep = value.toFixed(2).replace('.', ',');
    return format
      .replace('{{amount}}',                    amount)
      .replace('{{amount_no_decimals}}',         amountNoDecimal)
      .replace('{{amount_with_comma_separator}}', amountCommaSep);
  },

  /** Fetch wrapper with JSON */
  async fetchJSON(url, options = {}) {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      ...options
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  },

  /** Trap focus within element */
  trapFocus(element) {
    const focusableEls = element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusableEls[0];
    const last  = focusableEls[focusableEls.length - 1];

    function onKeyDown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    element.addEventListener('keydown', onKeyDown);
    first && first.focus();
    return () => element.removeEventListener('keydown', onKeyDown);
  },

  /** Remove focus trap */
  removeTrapFocus(element) {
    element.removeEventListener('keydown', () => {});
  },

  /** Open overlay — tracked per-owner (Set) so one drawer/modal closing
   *  doesn't rip the overlay out from under another that's still open on top
   *  of it (e.g. quick-add modal opened over the cart drawer). Pass a stable
   *  owner id per component; overlay only hides once every owner has closed. */
  _overlayOwners: new Set(),

  openOverlay(owner = 'default') {
    this._overlayOwners.add(owner);
    document.getElementById('overlay')?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  },

  /** Close overlay */
  closeOverlay(owner = 'default') {
    this._overlayOwners.delete(owner);
    if (this._overlayOwners.size > 0) return;
    document.getElementById('overlay')?.classList.remove('is-visible');
    document.body.style.overflow = '';
  },

  /** Dispatch custom event */
  dispatch(name, detail = {}, target = document) {
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true, detail }));
  },

  /** In-memory cache of complete-look HTML per product id — used only to paint
   *  instantly and avoid the hidden→fetch→show flash. Never trusted on its
   *  own: every load still re-fetches and corrects the DOM if the fresh
   *  result differs, so a genuinely different product can never get stuck
   *  showing another product's stale recommendations. */
  _completeLookCache: {},

  /** Fetch and fill any [data-complete-look] placeholders within root (cart
   *  "Complete The Look" upsell — see sections/cart-complete-look.liquid) */
  async loadCompleteLook(root = document) {
    const wraps = root.querySelectorAll('[data-complete-look]:not([data-cl-loaded])');
    for (const wrap of wraps) {
      wrap.dataset.clLoaded = 'true';
      const productId = wrap.dataset.productId;
      if (!productId) { wrap.hidden = true; continue; }

      // Paint the cached guess immediately (if any) — avoids the flash on a
      // no-op reopen. Still verified against a fresh fetch below.
      const cachedHTML = this._completeLookCache[productId];
      if (cachedHTML) {
        wrap.innerHTML = cachedHTML;
        wrap.hidden = false;
      }

      try {
        const url = `${window.flcForge.routes.product_recommendations_url}?section_id=cart-complete-look&product_id=${productId}&intent=complementary&limit=6`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById('cart-complete-look');
        const innerHTML = (fresh && fresh.innerHTML.trim()) ? fresh.innerHTML : '';
        this._completeLookCache[productId] = innerHTML;

        // Only touch the DOM again if the fetch disagrees with what's already
        // showing — keeps the common "nothing changed" case flash-free while
        // still always correcting a wrong guess.
        if (innerHTML !== (cachedHTML || '')) {
          wrap.innerHTML = innerHTML;
          wrap.hidden = !innerHTML;
        }
      } catch (err) {
        console.error('Complete the look fetch failed:', err);
      }
    }
  }
};

window.FLC = FLC;

/* Overlay click → close all drawers
   ========================================================================== */
document.getElementById('overlay')?.addEventListener('click', () => {
  FLC.dispatch('flc:drawers:close-all');
});

/* Announce to screen readers
   ========================================================================== */
class LiveRegion {
  constructor() {
    this.el = Object.assign(document.createElement('p'), {
      className: 'visually-hidden',
      'aria-live': 'polite',
      'aria-atomic': 'true'
    });
    document.body.appendChild(this.el);
  }

  announce(msg) {
    this.el.textContent = '';
    requestAnimationFrame(() => { this.el.textContent = msg; });
  }
}

window.liveRegion = new LiveRegion();

/* Header height CSS var
   ========================================================================== */
function setHeaderHeight() {
  const header = document.querySelector('.site-header');
  const announcementBar = document.querySelector('.announcement-bar');
  const isFixedHeader = header?.dataset.headerPosition === 'fixed';
  const isAnnouncementVisible = !!(isFixedHeader && announcementBar && !announcementBar.classList.contains('is-hidden'));
  const headerHeight = header ? header.offsetHeight : 0;
  const announcementHeight = isAnnouncementVisible ? announcementBar.offsetHeight : 0;
  const totalOffset = headerHeight + announcementHeight;

  document.documentElement.style.setProperty('--header-height', `${totalOffset}px`);
  document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
  document.documentElement.style.setProperty('--header-top', `${announcementHeight}px`);
  document.documentElement.style.setProperty('--header-offset', `${totalOffset}px`);
}

const resizeObserver = new ResizeObserver(FLC.debounce(setHeaderHeight, 100));

function observeHeaderElements() {
  resizeObserver.disconnect();
  const h = document.querySelector('.site-header');
  const a = document.querySelector('.announcement-bar');
  if (h) resizeObserver.observe(h);
  if (a) resizeObserver.observe(a);
  setHeaderHeight();
}

observeHeaderElements();

/* Sticky header
   ========================================================================== */
function initStickyHeader() {
  const header = document.querySelector('.site-header--sticky');
  if (!header) return;
  const announcementBar = document.querySelector('.announcement-bar');
  const isFixedHeader = header.dataset.headerPosition === 'fixed';

  if (isFixedHeader && announcementBar) {
    announcementBar.classList.add('announcement-bar--fixed');
  }

  const syncHeaderState = () => {
    const currentScrollY = window.scrollY;
    const isScrolled = currentScrollY > 80;

    header.classList.toggle('is-scrolled', isScrolled);

    setHeaderHeight();
  };

  let ticking = false;

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        syncHeaderState();
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });

  syncHeaderState();
}

initStickyHeader();

/* Transparent header
   ========================================================================== */
function initTransparentHeader() {
  const header = document.querySelector('.site-header');
  const main = document.getElementById('MainContent');
  if (!header || !main) return;

  const enableOnHome = header.dataset.transparentHome === 'true';
  const enableOnHeroFirst = header.dataset.transparentHeroFirst === 'true';
  if (!enableOnHome && !enableOnHeroFirst) return;

  const isHome = document.body.classList.contains('template-index');
  const firstSection = [...main.children].find((el) => el.nodeType === 1);
  const firstSectionIsHero = !!firstSection && (
    firstSection.matches('[data-overlay-header], .section-hero, .section-hero-video') ||
    firstSection.querySelector?.('[data-overlay-header], .section-hero, .section-hero-video')
  );

  const canBeTransparent = (enableOnHome && isHome) || (enableOnHeroFirst && firstSectionIsHero);
  if (!canBeTransparent) return;

  header.classList.add('can-be-transparent');
  document.body.classList.add('has-transparent-header');

  const syncTransparency = () => {
    const shouldBeTransparent = window.scrollY <= 10 && !header.classList.contains('search-active');
    header.classList.toggle('is-transparent', shouldBeTransparent);
  };

  syncTransparency();
  window.addEventListener('scroll', FLC.throttle(syncTransparency, 50), { passive: true });
}

initTransparentHeader();

/* Announcement bar auto-close
   ========================================================================== */
function initAnnouncementBarClose() {
  document.querySelectorAll('.announcement-bar__close').forEach(btn => {
    if (btn._flcBound) return;
    btn._flcBound = true;
    btn.addEventListener('click', () => {
      btn.closest('.announcement-bar')?.remove();
      setHeaderHeight();
    });
  });
}

initAnnouncementBarClose();

/* Search toggle
   ========================================================================== */
class SearchToggle extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-search-toggle]');
    this.form = document.querySelector('.header-search');
    this.btn?.addEventListener('click', () => this.toggle());
    this.form?.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());
    document.addEventListener('flc:drawers:close-all', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    this._onDocClick = (e) => {
      if (!this.form?.classList.contains('is-open')) return;
      const header = document.querySelector('.site-header');
      if (!header?.contains(e.target)) this.close();
    };
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
  }

  toggle() { this.form?.classList.contains('is-open') ? this.close() : this.open(); }

  open() {
    this.form?.classList.add('is-open');
    this.btn?.setAttribute('aria-expanded', 'true');
    document.querySelector('.site-header')?.classList.add('search-active', 'is-scrolled');
    FLC.openOverlay('search-toggle');
    requestAnimationFrame(() => this.form?.querySelector('[data-search-input]')?.focus());
  }

  close() {
    this.form?.classList.remove('is-open');
    this.btn?.setAttribute('aria-expanded', 'false');
    const header = document.querySelector('.site-header');
    header?.classList.remove('search-active');
    header?.classList.remove('is-scrolled');
    FLC.closeOverlay('search-toggle');
  }
}

customElements.define('search-toggle', SearchToggle);

/* Size guide drawer
   ========================================================================== */
function sizeGuideClose(drawer) {
  drawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('size-guide-mobile-open');
  FLC.closeOverlay('size-guide-drawer');
}

document.addEventListener('click', (e) => {
  // Open
  const openBtn = e.target.closest('[data-size-guide-open]');
  if (openBtn) {
    const id      = openBtn.dataset.sizeGuideOpen;
    const drawer  = document.getElementById(`size-guide-drawer-${id}`);
    if (!drawer) return;

    const contentEl   = drawer.querySelector('.size-guide-drawer__content');
    const pageHandle  = drawer.dataset.pageHandle;

    // Lazy-load page content on first open — extract #MainContent from the page
    if (pageHandle && contentEl && !drawer.dataset.loaded) {
      fetch(`/pages/${pageHandle}`)
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const main = doc.getElementById('MainContent');
          contentEl.innerHTML = main ? main.innerHTML : '';
          drawer.dataset.loaded = 'true';
        })
        .catch(() => {});
    }

    if (window.innerWidth <= 749) {
      document.body.classList.add('size-guide-mobile-open');
    }

    drawer.setAttribute('aria-hidden', 'false');
    FLC.openOverlay('size-guide-drawer');
    FLC.trapFocus(drawer);
    return;
  }

  // Close via close button
  const closeBtn = e.target.closest('[data-size-guide-close]');
  if (closeBtn) {
    const id = closeBtn.dataset.sizeGuideClose;
    const drawer = document.getElementById(`size-guide-drawer-${id}`);
    if (drawer) sizeGuideClose(drawer);
    return;
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('.size-guide-drawer[aria-hidden="false"]').forEach(sizeGuideClose);
});

document.addEventListener('flc:drawers:close-all', () => {
  const open = document.querySelectorAll('.size-guide-drawer[aria-hidden="false"]');
  open.forEach(d => d.setAttribute('aria-hidden', 'true'));
  if (open.length) FLC.closeOverlay('size-guide-drawer');
  document.body.classList.remove('size-guide-mobile-open');
});

/* Video autoplay on scroll
   ========================================================================== */
const videoObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    const video = entry.target;
    if (entry.isIntersecting) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  });
}, { threshold: 0.25 });

document.querySelectorAll('video[data-autoplay]').forEach(v => videoObserver.observe(v));

/* Product Card Image Carousel
   ========================================================================== */
(function initProductCardCarousels() {
  function initCard(card) {
    const carousel = card.querySelector('[data-pc-carousel]');
    if (!carousel) return;

    const slides  = [...carousel.querySelectorAll('.product-card__carousel-slide')];
    const dots    = [...card.querySelectorAll('.product-card__carousel-dot')];
    const prevBtn = card.querySelector('[data-pc-prev]');
    const nextBtn = card.querySelector('[data-pc-next]');
    let current   = 0;

    function goTo(index) {
      const i = Math.max(0, Math.min(index, slides.length - 1));
      carousel.scrollLeft = carousel.clientWidth * i;
      dots.forEach((d, j) => d.classList.toggle('is-active', j === i));
      current = i;
    }

    prevBtn?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(current - 1); });
    nextBtn?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(current + 1); });

    // Click on carousel (not a button) — navigate to product
    const productUrl = carousel.dataset.pcUrl;
    carousel.addEventListener('click', (e) => {
      if (!e.target.closest('[data-pc-prev],[data-pc-next]') && productUrl) {
        window.location.href = productUrl;
      }
    });

    // ---- Touch (mobile): stop propagation to outer slider ----
    carousel.addEventListener('touchstart', (e) => { e.stopPropagation(); }, { passive: true });
    carousel.addEventListener('touchmove',  (e) => { e.stopPropagation(); }, { passive: true });
    carousel.addEventListener('touchend',   (e) => {
      e.stopPropagation();
      // Snap to nearest slide after native scroll settles
      setTimeout(() => {
        if (!carousel.clientWidth) return;
        const i = Math.round(carousel.scrollLeft / carousel.clientWidth);
        goTo(Math.max(0, Math.min(i, slides.length - 1)));
      }, 50);
    }, { passive: true });

    // Sync dots on any scroll
    carousel.addEventListener('scroll', FLC.debounce(() => {
      if (!carousel.clientWidth) return;
      const i = Math.round(carousel.scrollLeft / carousel.clientWidth);
      if (i !== current) { dots.forEach((d, j) => d.classList.toggle('is-active', j === i)); current = i; }
    }, 80), { passive: true });
  }

  // Init all cards already in DOM
  document.querySelectorAll('.product-card').forEach(initCard);

  // Init cards injected later (e.g. recommendations AJAX, quick-view)
  const observer = new MutationObserver(mutations => {
    mutations.forEach(m => {
      m.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches('.product-card')) { initCard(node); }
        else { node.querySelectorAll?.('.product-card').forEach(initCard); }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();

/* Unlock drawer / modal transitions after first paint
   ========================================================================== */
// Two nested rAF calls ensure we are past the first paint commit before
// adding the class that enables CSS transitions. This prevents all drawers
// (cart, filter, mobile menu) from playing their slide animation on every
// page load or refresh.
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    document.documentElement.classList.add('is-page-loaded');
  });
});

/* Animate on scroll (simple)
   ========================================================================== */
if ('IntersectionObserver' in window) {
  const aoObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        aoObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('[data-animate]').forEach(el => aoObserver.observe(el));
}

/* Product Card — Sibling Image Swap on Swatch Hover
   ========================================================================== */
(function initSiblingImageSwap() {
  function initCard(card) {
    const siblingsEl = card.querySelector('.product-card__siblings');
    if (!siblingsEl || siblingsEl._flcSiblingBound) return;
    siblingsEl._flcSiblingBound = true;

    const mainImg  = card.querySelector('.product-card__img');
    const hoverImg = card.querySelector('.product-card__img--hover');
    if (!mainImg) return;

    siblingsEl.addEventListener('mouseover', (e) => {
      const link = e.target.closest('[data-sibling-image]');
      if (!link) return;
      const url = link.dataset.siblingImage;
      if (!url) return;

      if (!mainImg.dataset.originalSrc) {
        mainImg.dataset.originalSrc    = mainImg.src;
        mainImg.dataset.originalSrcset = mainImg.srcset;
      }
      mainImg.srcset = '';
      mainImg.src    = url;

      if (hoverImg) {
        if (!hoverImg.dataset.originalSrc) {
          hoverImg.dataset.originalSrc    = hoverImg.src;
          hoverImg.dataset.originalSrcset = hoverImg.srcset;
        }
        hoverImg.srcset = '';
        hoverImg.src    = url;
      }
    });

    siblingsEl.addEventListener('mouseleave', () => {
      if (mainImg.dataset.originalSrc) {
        mainImg.src    = mainImg.dataset.originalSrc;
        mainImg.srcset = mainImg.dataset.originalSrcset || '';
      }
      if (hoverImg?.dataset.originalSrc) {
        hoverImg.src    = hoverImg.dataset.originalSrc;
        hoverImg.srcset = hoverImg.dataset.originalSrcset || '';
      }
    });
  }

  document.querySelectorAll('.product-card').forEach(initCard);

  new MutationObserver(mutations => {
    mutations.forEach(m => {
      m.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches('.product-card')) initCard(node);
        else node.querySelectorAll?.('.product-card').forEach(initCard);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

/* Complete The Look — load whatever placeholder(s) are already in the DOM
   (cart drawer's initial server-rendered state, full cart page on load)
   ========================================================================== */
FLC.loadCompleteLook();

/* Theme Editor — re-init on section reload
   ========================================================================== */
document.addEventListener('shopify:section:load', () => {
  observeHeaderElements();
  initStickyHeader();
  initTransparentHeader();
  initAnnouncementBarClose();
});

